'use client'

import React from 'react'
import {
  ArrowRight,
  Shield,
  CheckCircle,
  XCircle,
  Brain,
  Plane,
  Users,
  Database,
  Lock,
  Bot,
  User,
  FileCode,
  Layers,
  Eye,
  Rocket,
  Timer,
  FileCheck,
  BarChart3,
  DollarSign,
  Clock,
  MessageSquare,
  Map,
  KeyRound,
  Server,
  BookOpen,
} from 'lucide-react'

/**
 * Lakbay AI Case Study Diagrams
 * Visual components for the Thread-Based Engineering in Production blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

// 1. Timeline Compression — The Headline Visual
export function TimelineCompressionDiagram() {
  const timelines = [
    {
      label: 'Solo Developer',
      sublabel: 'Traditional',
      months: 6,
      display: '5-7 months',
      source: 'Cleveroad, FullScale',
      color: '#ef4444',
    },
    {
      label: 'Agency Team',
      sublabel: '2-3 developers',
      months: 3.5,
      display: '3-4 months',
      source: 'UX Continuum, COAX',
      color: '#f97316',
    },
    {
      label: 'AI-Assisted Team',
      sublabel: 'Copilot-level tools',
      months: 2.5,
      display: '2-3 months',
      source: 'METR-adjusted',
      color: '#FDC304',
    },
    {
      label: 'Z-Thread',
      sublabel: 'Thread-Based Eng.',
      months: 0.033,
      display: '1 DAY',
      source: 'Lakbay AI (actual)',
      color: '#22c55e',
    },
  ]

  const maxMonths = 7

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          TIMELINE COMPRESSION: 70x FASTER
        </h3>
        <p className='text-sm text-muted-foreground'>
          Same scope, same complexity — different methodology
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Bar chart */}
        <div className='w-full max-w-lg space-y-3'>
          {timelines.map((t, idx) => (
            <div key={idx} className='space-y-1'>
              <div className='flex items-center justify-between'>
                <div>
                  <span className='text-xs md:text-sm font-medium text-foreground'>
                    {t.label}
                  </span>
                  <span className='text-[10px] text-muted-foreground ml-2'>
                    {t.sublabel}
                  </span>
                </div>
                <span
                  className='text-xs md:text-sm font-bold'
                  style={{ color: t.color }}
                >
                  {t.display}
                </span>
              </div>
              <div className='relative h-7 bg-muted rounded-lg overflow-hidden'>
                <div
                  className='absolute top-0 left-0 h-full rounded-lg flex items-center justify-end pr-2 transition-all duration-500'
                  style={{
                    width: `${Math.max((t.months / maxMonths) * 100, 3)}%`,
                    backgroundColor: t.color,
                  }}
                >
                  {t.months > 1 && (
                    <span className='text-[10px] font-bold text-white'>
                      {t.display}
                    </span>
                  )}
                </div>
                {t.months < 1 && (
                  <div className='absolute top-0 left-4 h-full flex items-center'>
                    <span
                      className='text-[10px] font-bold'
                      style={{ color: t.color }}
                    >
                      ← 1 day
                    </span>
                  </div>
                )}
              </div>
              <p className='text-[10px] text-muted-foreground text-right'>
                Source: {t.source}
              </p>
            </div>
          ))}
        </div>

        {/* Compression stats */}
        <div className='grid grid-cols-3 gap-3 w-full max-w-lg'>
          <div className='p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-center'>
            <p className='text-2xl md:text-3xl font-bold text-green-500'>70x</p>
            <p className='text-[10px] text-muted-foreground'>vs agency team</p>
          </div>
          <div className='p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-center'>
            <p className='text-2xl md:text-3xl font-bold text-green-500'>
              130x
            </p>
            <p className='text-[10px] text-muted-foreground'>
              vs solo developer
            </p>
          </div>
          <div className='p-3 rounded-lg bg-[#008dcc]/10 border border-[#008dcc]/30 text-center'>
            <p className='text-2xl md:text-3xl font-bold text-[#008dcc]'>0</p>
            <p className='text-[10px] text-muted-foreground'>
              critical vulnerabilities
            </p>
          </div>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-green-500/10 rounded-lg border border-green-500/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>Not a percentage improvement</span>{' '}
            — Z-threads replace the workflow entirely. The review loop that
            makes standard AI tools take months is eliminated by governance.
          </p>
        </div>
      </div>
    </div>
  )
}

// 2. Scope Delivered — Everything Shipped in 1 Day
export function ScopeDeliveredDiagram() {
  const features = [
    {
      icon: Brain,
      title: 'RAG Pipeline',
      detail: 'pgvector + OpenAI embeddings',
      color: '#8b5cf6',
    },
    {
      icon: MessageSquare,
      title: 'Streaming AI Chat',
      detail: 'GPT-4o-mini + structured output',
      color: '#008dcc',
    },
    {
      icon: Plane,
      title: 'Flight Search',
      detail: 'Amadeus API, 15+ airports',
      color: '#F90B8A',
    },
    {
      icon: Users,
      title: '3 Portals',
      detail: 'Traveler, Agent, Admin',
      color: '#FDC304',
    },
    {
      icon: KeyRound,
      title: 'Dual Auth',
      detail: 'Clerk + NextAuth',
      color: '#10b981',
    },
    {
      icon: Database,
      title: '8+ DB Tables',
      detail: 'RLS on every table',
      color: '#ef4444',
    },
    {
      icon: Server,
      title: '20+ API Routes',
      detail: 'Zod validation',
      color: '#008dcc',
    },
    {
      icon: Map,
      title: '18 Destinations',
      detail: 'Curated content sets',
      color: '#FDC304',
    },
    {
      icon: BookOpen,
      title: '13 Blog Posts',
      detail: 'Contentlayer2 + MDX',
      color: '#8b5cf6',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          SHIPPED IN 1 DAY
        </h3>
        <p className='text-sm text-muted-foreground'>
          Not a prototype. A production application with auth, security, and
          live demo.
        </p>
      </div>

      <div className='grid grid-cols-3 gap-3'>
        {features.map((f, idx) => (
          <div
            key={idx}
            className='p-3 rounded-xl bg-background border border-border/60 text-center'
          >
            <div
              className='w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center'
              style={{ backgroundColor: `${f.color}20` }}
            >
              <f.icon className='w-5 h-5' style={{ color: f.color }} />
            </div>
            <p className='text-xs font-semibold text-foreground'>{f.title}</p>
            <p className='text-[10px] text-muted-foreground'>{f.detail}</p>
          </div>
        ))}
      </div>

      {/* Bottom stat */}
      <div className='mt-4 px-4 py-3 rounded-xl bg-[#008dcc]/10 border border-[#008dcc]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-[#008dcc]'>
            Traditional estimate for this scope:
          </span>{' '}
          900-1,400 development hours / $60,000-$150,000 agency cost
        </p>
      </div>
    </div>
  )
}

// 3. Z-Thread Flow — Standard Dev Loop vs Z-Thread
export function ZThreadFlowDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          STANDARD AI vs Z-THREAD EXECUTION
        </h3>
        <p className='text-sm text-muted-foreground'>
          The review loop is the bottleneck — Z-threads eliminate it
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Standard AI-Assisted */}
        <div className='w-full'>
          <div className='flex items-center gap-2 mb-3'>
            <XCircle className='w-5 h-5 text-red-500' />
            <span className='text-red-500 font-semibold text-sm uppercase tracking-wide'>
              Standard AI-Assisted Development
            </span>
          </div>
          <div className='p-4 rounded-xl bg-red-500/10 border border-red-500/30'>
            <div className='flex flex-wrap items-center justify-center gap-2'>
              {[
                { label: 'Write', icon: User },
                { label: 'AI Suggests', icon: Bot },
                { label: 'Review', icon: Eye, highlight: true },
                { label: 'Edit', icon: FileCode, highlight: true },
                { label: 'Repeat', icon: Timer, highlight: true },
              ].map((step, index, arr) => (
                <React.Fragment key={step.label}>
                  <div
                    className={`flex flex-col items-center p-2 rounded-lg ${step.highlight ? 'bg-red-500/20 border border-red-500/50' : 'bg-muted'}`}
                  >
                    <step.icon
                      className={`w-5 h-5 ${step.highlight ? 'text-red-500' : 'text-muted-foreground'}`}
                    />
                    <span className='text-[10px] text-foreground mt-1'>
                      {step.label}
                    </span>
                  </div>
                  {index < arr.length - 1 && (
                    <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className='flex items-center justify-center gap-2 mt-3'>
              <span className='text-red-500 text-xs'>
                56% rejection rate → edit-review-regenerate loop → months
              </span>
            </div>
          </div>
        </div>

        {/* Z-Thread */}
        <div className='w-full'>
          <div className='flex items-center gap-2 mb-3'>
            <CheckCircle className='w-5 h-5 text-green-500' />
            <span className='text-green-600 dark:text-green-400 font-semibold text-sm uppercase tracking-wide'>
              Z-Thread Execution
            </span>
          </div>
          <div className='p-4 rounded-xl bg-green-500/10 border border-green-500/30'>
            <div className='flex flex-wrap items-center justify-center gap-2'>
              {[
                {
                  label: 'CLAUDE.md',
                  icon: Shield,
                  highlight: true,
                  note: 'Human shows up here',
                },
                { label: 'AI Executes', icon: Bot },
                {
                  label: 'Governance Verifies',
                  icon: CheckCircle,
                  highlight: true,
                },
                { label: 'Ship', icon: Rocket },
              ].map((step, index, arr) => (
                <React.Fragment key={step.label}>
                  <div
                    className={`flex flex-col items-center p-2 rounded-lg ${step.highlight ? 'bg-green-500/20 border border-green-500/50' : 'bg-muted'}`}
                  >
                    <step.icon
                      className={`w-5 h-5 ${step.highlight ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}
                    />
                    <span className='text-[10px] text-foreground mt-1'>
                      {step.label}
                    </span>
                  </div>
                  {index < arr.length - 1 && (
                    <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className='text-center text-green-600 dark:text-green-400 text-xs mt-3'>
              Human judgment front-loaded into governance → no review loop → 1
              day
            </p>
          </div>
        </div>

        {/* Key insight */}
        <div className='px-4 py-3 rounded-xl bg-[#FDC304]/10 border border-[#FDC304]/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold text-[#FDC304]'>
              The bottleneck was never coding speed.
            </span>{' '}
            It was the review loop. Z-threads eliminate it by making the wrong
            code impossible to generate.
          </p>
        </div>
      </div>
    </div>
  )
}

// 4. Governance Scorecard — Industry Crisis vs Lakbay AI
export function GovernanceScorecardDiagram() {
  const metrics = [
    {
      metric: 'Vulnerability Rate',
      industry: '45%',
      industryLabel: 'of AI code',
      lakbay: '0',
      lakbayLabel: 'critical at launch',
      how: 'CLAUDE.md + Snyk',
      icon: Shield,
    },
    {
      metric: 'Code Churn',
      industry: '41%',
      industryLabel: 'revised in 2 weeks',
      lakbay: 'Stable',
      lakbayLabel: 'production day 1',
      how: 'Governance-first',
      icon: BarChart3,
    },
    {
      metric: 'Hardcoded Secrets',
      industry: 'Common',
      industryLabel: 'in AI code',
      lakbay: '0',
      lakbayLabel: 'all env variables',
      how: 'CLAUDE.md ban',
      icon: KeyRound,
    },
    {
      metric: 'Database Security',
      industry: 'Often missing',
      industryLabel: 'RLS absent',
      lakbay: '100%',
      lakbayLabel: 'RLS on all tables',
      how: 'CLAUDE.md mandate',
      icon: Lock,
    },
    {
      metric: 'Input Validation',
      industry: 'Frequently absent',
      industryLabel: 'in AI output',
      lakbay: 'Zod',
      lakbayLabel: 'on API inputs',
      how: 'CLAUDE.md requirement',
      icon: FileCheck,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          GOVERNANCE SCORECARD
        </h3>
        <p className='text-sm text-muted-foreground'>
          Industry crisis metrics vs. Lakbay AI production results
        </p>
      </div>

      <div className='space-y-3'>
        {metrics.map(m => (
          <div
            key={m.metric}
            className='bg-background rounded-xl p-4 border border-border/60'
          >
            <div className='flex items-center gap-2 mb-3'>
              <m.icon className='w-4 h-4 text-[#008dcc]' />
              <span className='text-foreground font-semibold text-sm'>
                {m.metric}
              </span>
              <span className='ml-auto text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-600 dark:text-green-400'>
                {m.how}
              </span>
            </div>

            <div className='grid grid-cols-3 gap-3'>
              {/* Industry */}
              <div className='text-center'>
                <div className='text-[10px] text-muted-foreground uppercase mb-1'>
                  Industry
                </div>
                <div className='text-lg font-bold text-red-500'>
                  {m.industry}
                </div>
                <div className='text-[10px] text-muted-foreground'>
                  {m.industryLabel}
                </div>
              </div>

              {/* Arrow */}
              <div className='flex items-center justify-center'>
                <ArrowRight className='w-5 h-5 text-green-500' />
              </div>

              {/* Lakbay AI */}
              <div className='text-center'>
                <div className='text-[10px] text-muted-foreground uppercase mb-1'>
                  Lakbay AI
                </div>
                <div className='text-lg font-bold text-green-600 dark:text-green-400'>
                  {m.lakbay}
                </div>
                <div className='text-[10px] text-muted-foreground'>
                  {m.lakbayLabel}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Key insight */}
      <div className='mt-4 px-4 py-2 bg-green-500/10 rounded-lg border border-green-500/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold'>
            88% of developers report negative AI impacts on technical debt.
          </span>{' '}
          Lakbay AI proves governance-first development eliminates this
          entirely.
        </p>
      </div>
    </div>
  )
}

// 5. Cost Compression — Financial Impact
export function CostCompressionDiagram() {
  const scenarios = [
    {
      label: 'Solo Developer',
      sublabel: 'Traditional',
      cost: '$40K-$70K',
      timeline: '5-7 months',
      color: '#ef4444',
      barWidth: 85,
    },
    {
      label: 'Agency Team',
      sublabel: '2-3 developers',
      cost: '$60K-$150K',
      timeline: '3-4 months',
      color: '#f97316',
      barWidth: 100,
    },
    {
      label: 'AI-Assisted Team',
      sublabel: 'With Copilot',
      cost: '$40K-$100K',
      timeline: '2-3 months',
      color: '#FDC304',
      barWidth: 70,
    },
    {
      label: 'Z-Thread',
      sublabel: 'Thread-Based Eng.',
      cost: '~$0*',
      timeline: '1 day',
      color: '#22c55e',
      barWidth: 3,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          COST COMPRESSION
        </h3>
        <p className='text-sm text-muted-foreground'>
          From $60K-$150K agency cost to developer time + API costs
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Cost bars */}
        <div className='w-full max-w-lg space-y-3'>
          {scenarios.map((s, idx) => (
            <div key={idx} className='space-y-1'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <DollarSign className='w-4 h-4' style={{ color: s.color }} />
                  <span className='text-xs font-medium text-foreground'>
                    {s.label}
                  </span>
                </div>
                <div className='text-right'>
                  <span
                    className='text-sm font-bold'
                    style={{ color: s.color }}
                  >
                    {s.cost}
                  </span>
                  <span className='text-[10px] text-muted-foreground ml-2'>
                    / {s.timeline}
                  </span>
                </div>
              </div>
              <div className='relative h-5 bg-muted rounded-lg overflow-hidden'>
                <div
                  className='absolute top-0 left-0 h-full rounded-lg'
                  style={{
                    width: `${s.barWidth}%`,
                    backgroundColor: s.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <p className='text-[10px] text-muted-foreground'>
          *Developer time + OpenAI/Supabase API costs. Sources: Ideas2It,
          SpaceOTechnologies, Cleveroad
        </p>

        {/* Business model shift callout */}
        <div className='w-full max-w-lg p-4 rounded-xl bg-[#008dcc]/10 border border-[#008dcc]/30'>
          <div className='flex items-start gap-3'>
            <div className='w-8 h-8 rounded-full bg-[#008dcc] flex items-center justify-center flex-shrink-0'>
              <Clock className='w-4 h-4 text-white' />
            </div>
            <div>
              <p className='text-sm font-bold text-foreground mb-1'>
                Business Model Shift
              </p>
              <p className='text-xs text-muted-foreground'>
                When production AI platforms can be built in days instead of
                months, the bottleneck moves from development to strategy. The
                question changes from "can we afford to build this?" to "what
                should we build next?"
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// 6. Thread Progression — Path to Earning Z-Threads
export function ThreadProgressionDiagram() {
  const weeks = [
    {
      week: 1,
      title: 'Base Threads',
      description: 'Full human review, build CLAUDE.md',
      icon: Eye,
      color: '#008dcc',
      tasks: [
        'Single prompt + full review',
        'Build CLAUDE.md governance',
        'Establish security rules',
      ],
    },
    {
      week: 2,
      title: 'P-Threads',
      description: 'Parallel agents, maintain review',
      icon: Layers,
      color: '#FDC304',
      tasks: [
        '2-3 AI instances simultaneously',
        'Keep full human review',
        'Observe governance patterns',
      ],
    },
    {
      week: 3,
      title: 'Test-Driven',
      description: 'Automated checks replace review',
      icon: FileCheck,
      color: '#8b5cf6',
      tasks: [
        'Linting, type checking, Snyk',
        'Measure: % passing auto-checks',
        'Reduce manual review',
      ],
    },
    {
      week: '4+',
      title: 'Z-Threads',
      description: 'Earned autonomy for proven tasks',
      icon: Rocket,
      color: '#22c55e',
      tasks: [
        '>95% passing automated checks',
        'Zero-touch on bounded tasks',
        'Human oversight on novel patterns',
      ],
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE PATH TO Z-THREADS
        </h3>
        <p className='text-sm text-muted-foreground'>
          Autonomy is earned through governance, not granted by default
        </p>
      </div>

      {/* Timeline */}
      <div className='relative'>
        {/* Connection line - desktop */}
        <div className='hidden md:block absolute top-8 left-[7%] right-[7%] h-1 bg-gradient-to-r from-[#008dcc] via-[#FDC304] via-[#8b5cf6] to-[#22c55e] rounded-full' />

        <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
          {weeks.map((week, index) => (
            <div key={index} className='relative'>
              {/* Week indicator */}
              <div className='flex flex-col items-center mb-3'>
                <div
                  className='w-10 h-10 rounded-full flex items-center justify-center border-4 border-background z-10'
                  style={{ backgroundColor: week.color }}
                >
                  <week.icon className='w-5 h-5 text-white' />
                </div>
                <span
                  className='text-[10px] font-bold mt-1'
                  style={{ color: week.color }}
                >
                  WEEK {week.week}
                </span>
              </div>

              {/* Content card */}
              <div
                className='bg-background rounded-xl p-3 border-t-2 border border-border/60'
                style={{ borderTopColor: week.color }}
              >
                <h4 className='text-foreground font-semibold text-sm mb-1'>
                  {week.title}
                </h4>
                <p className='text-muted-foreground text-[10px] mb-2'>
                  {week.description}
                </p>
                <ul className='space-y-1'>
                  {week.tasks.map(task => (
                    <li
                      key={task}
                      className='text-[10px] text-muted-foreground flex items-start gap-1'
                    >
                      <CheckCircle
                        className='w-3 h-3 mt-0.5 flex-shrink-0'
                        style={{ color: week.color }}
                      />
                      {task}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Arrow for mobile */}
              {index < weeks.length - 1 && (
                <div className='md:hidden flex justify-center my-2'>
                  <ArrowRight className='w-4 h-4 text-muted-foreground rotate-90' />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Key metric */}
      <div className='mt-6 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold'>The key metric:</span> When &gt;95% of
          AI output passes automated governance checks for a task type, that
          task type becomes a Z-thread candidate.
        </p>
      </div>
    </div>
  )
}

// 7. Tech Stack Architecture — 3-Layer Diagram
export function TechStackDiagram() {
  const layers = [
    {
      name: 'AI LAYER',
      color: '#8b5cf6',
      items: [
        { label: 'OpenAI GPT-4o-mini', detail: 'Streaming chat' },
        { label: 'text-embedding-3-small', detail: 'RAG embeddings' },
        { label: 'Supabase pgvector', detail: '1536-dim similarity search' },
        { label: 'Amadeus API', detail: 'Real-time flight search' },
      ],
    },
    {
      name: 'APPLICATION LAYER',
      color: '#008dcc',
      items: [
        { label: 'Next.js 16', detail: 'App Router + TypeScript strict' },
        { label: 'Clerk + NextAuth', detail: 'Dual auth, 3 roles' },
        { label: '20+ API Routes', detail: 'Zod validation' },
        { label: 'Tailwind CSS 4', detail: 'Responsive UI' },
      ],
    },
    {
      name: 'DATA & CONTENT LAYER',
      color: '#10b981',
      items: [
        { label: 'Supabase PostgreSQL', detail: '8+ tables, RLS everywhere' },
        { label: 'localStorage', detail: 'Offline-first sync' },
        { label: 'Contentlayer2 + MDX', detail: '13 blog posts' },
        { label: '18 Destination Datasets', detail: 'Curated local knowledge' },
      ],
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          LAKBAY AI TECHNICAL ARCHITECTURE
        </h3>
        <p className='text-sm text-muted-foreground'>
          Three layers, one day — all governed by CLAUDE.md
        </p>
      </div>

      <div className='space-y-4'>
        {layers.map((layer, layerIdx) => (
          <div
            key={layer.name}
            className='rounded-xl border-l-4 border border-border/60 bg-background overflow-hidden'
            style={{ borderLeftColor: layer.color }}
          >
            {/* Layer header */}
            <div
              className='px-4 py-2 flex items-center gap-2'
              style={{ backgroundColor: `${layer.color}15` }}
            >
              <div
                className='w-6 h-6 rounded flex items-center justify-center'
                style={{ backgroundColor: layer.color }}
              >
                <span className='text-white font-bold text-[10px]'>
                  {layerIdx + 1}
                </span>
              </div>
              <span
                className='text-xs font-bold uppercase tracking-wider'
                style={{ color: layer.color }}
              >
                {layer.name}
              </span>
            </div>

            {/* Layer items */}
            <div className='grid grid-cols-2 gap-2 p-3'>
              {layer.items.map((item, idx) => (
                <div
                  key={idx}
                  className='flex items-start gap-2 p-2 rounded-lg bg-muted/50'
                >
                  <div
                    className='w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0'
                    style={{ backgroundColor: layer.color }}
                  />
                  <div>
                    <p className='text-xs font-medium text-foreground'>
                      {item.label}
                    </p>
                    <p className='text-[10px] text-muted-foreground'>
                      {item.detail}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Governance overlay */}
      <div className='mt-4 p-3 rounded-xl border-2 border-dashed border-[#FDC304] bg-[#FDC304]/5'>
        <div className='flex items-center justify-center gap-2'>
          <Shield className='w-4 h-4 text-[#FDC304]' />
          <span className='text-xs font-bold text-[#FDC304] uppercase tracking-wider'>
            CLAUDE.md Governance Layer
          </span>
          <Shield className='w-4 h-4 text-[#FDC304]' />
        </div>
        <p className='text-[10px] text-muted-foreground text-center mt-1'>
          Security boundaries, code quality standards, and workflow rules
          enforced across all three layers
        </p>
      </div>
    </div>
  )
}
